import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Sparkles, AlertTriangle, Wrench, Package, Clock, Lightbulb, ShieldAlert, Store, ArrowRight, Loader2, RotateCcw } from "lucide-react";
import { useApi, errMsg, money, specLabel } from "@/lib/api";
import { useMe } from "@/context/Me";
import { PhotoPicker, PageTitle, Tape } from "@/components/bits";
import NearbyStores from "@/components/NearbyStores";

const URG = { baixa: "bg-emerald-500/15 text-emerald-300", media: "bg-amber-500/15 text-amber-300", alta: "bg-[#FF5722]/20 text-[#FF5722]" };

export function Report({ d, country, stores = [] }) {
  const range = (a, b) => `${money(a, country)} – ${money(b, country)}`;
  return (
    <div data-testid="diagnosis-report" className="panel overflow-hidden fade-up">
      <Tape />
      <div className="p-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="eyebrow mb-1">Diagnóstico da obra · Zelobra</div>
            <h2 className="text-3xl font-black uppercase sm:text-4xl" data-testid="diagnosis-title">{d.titulo}</h2>
          </div>
          <span className={`rounded-full px-3 py-1 text-sm font-bold ${URG[d.urgencia] || URG.media}`} data-testid="diagnosis-urgency"><AlertTriangle size={14} className="mr-1 inline" />Urgência {{ baixa: "baixa", media: "média", alta: "alta" }[d.urgencia] || d.urgencia}</span>
        </div>
        <p className="mt-4 text-slate-300">{d.diagnostico}</p>
        {d.causas_provaveis?.length > 0 && <ul className="mt-3 list-inside list-disc text-sm text-slate-400">{d.causas_provaveis.map((c) => <li key={c}>{c}</li>)}</ul>}
        <div className="mt-6 grid gap-3 sm:grid-cols-3">
          <div className="rounded-xl border border-white/10 p-4"><Wrench size={18} className="text-[#FFC700]" /><div className="mt-2 text-xs uppercase text-slate-400">Profissional indicado</div><div className="text-lg font-bold" data-testid="diagnosis-pro">{specLabel(d.profissional_indicado, country)}</div></div>
          <div className="rounded-xl border border-white/10 p-4"><Clock size={18} className="text-[#FFC700]" /><div className="mt-2 text-xs uppercase text-slate-400">Mão de obra</div><div className="text-lg font-bold">~{d.mao_de_obra_horas}h · {range(d.custo_mao_de_obra_min, d.custo_mao_de_obra_max)}</div></div>
          <div className="rounded-xl bg-[#FFC700] p-4 text-[#111317]"><div className="text-xs font-bold uppercase">Custo total · valor aproximado</div><div className="font-display text-3xl font-black leading-tight" data-testid="diagnosis-total">{range(d.custo_total_min, d.custo_total_max)}</div></div>
        </div>
        <h3 className="mt-8 flex items-center gap-2 text-xl font-bold uppercase"><Package size={18} className="text-[#FFC700]" />Materiais</h3>
        <div className="mt-3 overflow-x-auto">
          <table className="w-full text-sm" data-testid="diagnosis-materials">
            <thead><tr className="border-b border-white/10 text-left text-xs uppercase text-slate-400"><th className="py-2">Item</th><th>Qtd.</th><th className="text-right">Custo</th></tr></thead>
            <tbody>{(d.materiais || []).map((m, i) => (
              <tr key={i} className="border-b border-white/5"><td className="py-2.5 pr-3">{m.item}</td><td className="pr-3 text-slate-400">{m.quantidade}</td><td className="whitespace-nowrap text-right font-mono">{range(m.custo_min, m.custo_max)}</td></tr>
            ))}</tbody>
          </table>
        </div>
        <div className="mt-6 grid gap-4 md:grid-cols-2">
          {d.dicas?.length > 0 && <div className="rounded-xl border border-white/10 p-4"><div className="mb-2 flex items-center gap-2 font-bold"><Lightbulb size={16} className="text-[#FFC700]" />Dicas</div><ul className="space-y-1 text-sm text-slate-300">{d.dicas.map((x) => <li key={x}>• {x}</li>)}</ul></div>}
          {d.seguranca && <div className="rounded-xl border border-[#FF5722]/30 bg-[#FF5722]/5 p-4"><div className="mb-2 flex items-center gap-2 font-bold text-[#FF5722]"><ShieldAlert size={16} />Segurança</div><p className="text-sm text-slate-300">{d.seguranca}</p></div>}
        </div>
        {stores.length > 0 && (
          <div className="mt-6">
            <div className="mb-2 flex items-center gap-2 font-bold"><Store size={16} className="text-[#FFC700]" />Lojas parceiras para os materiais</div>
            <div className="flex flex-wrap gap-2">{stores.map((s) => (
              <a key={s.id} href={s.url || undefined} target="_blank" rel="noreferrer" data-testid={`store-${s.id}`} className="rounded-xl border border-white/10 px-4 py-2 text-sm hover:border-[#FFC700]/60"><b>{s.name}</b>{s.city && <span className="text-slate-400"> · {s.city}</span>}{s.description && <div className="text-xs text-slate-400">{s.description}</div>}</a>
            ))}</div>
          </div>
        )}
        <p className="mt-6 text-xs text-slate-500">Avaliação técnica preliminar com base nas fotos e na descrição enviadas. O valor final é combinado com o profissional após a visita.</p>
      </div>
    </div>
  );
}

export default function Diagnose() {
  const api = useApi();
  const nav = useNavigate();
  const { me, country } = useMe();
  const [photos, setPhotos] = useState([]);
  const [desc, setDesc] = useState("");
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);

  const run = async () => {
    setBusy(true);
    try { const { data } = await api.post("/ai/diagnose", { photos, description: desc, country }); setRes(data); }
    catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  const toJob = () => {
    if (!me?.profile_type) return nav("/cadastro");
    const d = res.result;
    nav("/chamados/novo", { state: { title: d.titulo, description: `${desc ? desc + "\n\n" : ""}${d.diagnostico}`, specialty: d.profissional_indicado, photos: res.photos, diagnosis_id: res.id } });
  };

  return (
    <div className="mx-auto max-w-4xl px-4 py-8 sm:px-6">
      <PageTitle eyebrow="Avaliação técnica" title="Diagnóstico da obra" />
      {!res ? (
        <div className="panel space-y-6 p-6 fade-up">
          <div>
            <span className="label">Fotos do problema (até 3)</span>
            <PhotoPicker testid="diagnose-photo" value={photos} onChange={setPhotos} max={3} capture="environment" label="Tirar foto" />
          </div>
          <label className="block"><span className="label">Descreva em uma frase</span>
            <textarea data-testid="diagnose-description" className="field min-h-[90px]" value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="Ex.: mancha de umidade na parede do quarto que aumenta quando chove" /></label>
          <button data-testid="diagnose-submit" className="btn-hazard w-full py-4 text-lg" disabled={busy || (!photos.length && !desc.trim())} onClick={run}>
            {busy ? <><Loader2 className="animate-spin" />Analisando… (até 30s)</> : <><Sparkles />Gerar diagnóstico</>}
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          <Report d={res.result} country={country} stores={res.stores} />
          <NearbyStores title="Onde comprar os materiais" limit={6} />
          <div className="flex flex-wrap gap-3">
            {me?.profile_type !== "pro" && <button data-testid="diagnose-to-job" className="btn-hazard" onClick={toJob}>Publicar chamado com este diagnóstico <ArrowRight size={18} /></button>}
            <button data-testid="diagnose-new" className="btn-ghost" onClick={() => { setRes(null); setPhotos([]); setDesc(""); }}><RotateCcw size={16} />Novo diagnóstico</button>
          </div>
        </div>
      )}
    </div>
  );
}
